import type { Webinar, WebinarWhen, WebinarAttendee } from '@proref/types';
import type { CreateWebinarInput, UpdateWebinarInput } from '@proref/schemas';
import { api, unwrap, type Envelope } from '../lib/api';

// Webinars the caller may see (their federation's + platform-wide general ones), each with
// reservation_count + is_reserved + seats_left resolved server-side. 'upcoming' (default) is
// soonest-first; 'past' is most-recent-first, for the history tab.
export async function listWebinars(when: WebinarWhen = 'upcoming'): Promise<Webinar[]> {
  const res = await api.get<Envelope<Webinar[]>>('/webinars', { params: { when } });
  return unwrap(res.data);
}

// The host's "who's coming" roster for one webinar. Host-only — the API 403s anyone else.
export async function getWebinarAttendees(id: string): Promise<WebinarAttendee[]> {
  const res = await api.get<Envelope<WebinarAttendee[]>>(`/webinars/${id}/reservations`);
  return unwrap(res.data);
}

// Download the attendee roster as CSV (name/federation/licence/reserved-at). The response is a
// file, not the usual envelope, so it's fetched as a blob and handed to the browser as a download —
// same pattern as downloadExamResultsCsv in exams.service.ts.
export async function downloadWebinarAttendeesCsv(id: string, filename: string): Promise<void> {
  const res = await api.get(`/webinars/${id}/reservations/export`, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  // Revoking immediately would race the download on some browsers; a tick is enough for the click
  // to have been handed off.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export async function createWebinar(input: CreateWebinarInput): Promise<Webinar> {
  const res = await api.post<Envelope<Webinar>>('/webinars', input);
  return unwrap(res.data);
}

// Edit a webinar — chiefly how the meeting link gets added after the session was scheduled. A
// PATCH, so only the fields actually being changed are sent; an explicit null clears one.
export async function updateWebinar(id: string, input: UpdateWebinarInput): Promise<Webinar> {
  const res = await api.patch<Envelope<Webinar>>(`/webinars/${id}`, input);
  return unwrap(res.data);
}

// Reserve / cancel a seat. Both idempotent server-side, so the toggle can fire freely from a stale UI.
export async function reserveWebinar(id: string): Promise<void> {
  await api.post<Envelope<null>>(`/webinars/${id}/reserve`);
}

export async function cancelWebinarReservation(id: string): Promise<void> {
  await api.delete<Envelope<null>>(`/webinars/${id}/reserve`);
}

export async function deleteWebinar(id: string): Promise<void> {
  await api.delete<Envelope<null>>(`/webinars/${id}`);
}
